import { PDFDocument, rgb, StandardFonts, PDFFont } from 'pdf-lib';

// Helper function to convert image to base64
const loadImageAsBase64 = (imagePath: string): Promise<string> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    
    img.onload = () => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      
      canvas.width = img.width;
      canvas.height = img.height;
      ctx?.drawImage(img, 0, 0);
      
      const dataURL = canvas.toDataURL('image/png');
      resolve(dataURL);
    };
    
    img.onerror = (error) => {
      console.error('Error loading image:', error);
      reject(new Error('Failed to load image'));
    };
    
    img.src = imagePath;
  });
};

// Removed font loading function - using transliteration approach instead

// Transliteration fallback for Armenian text
const transliterateArmenian = (text: string): string => {
  // First handle full phrases, then individual characters
  const phrases: { [key: string]: string } = {
    'ՀՐԱՎԻՐԱՏՈՄՍ': 'INVITATION',
    'Միջոցառման ամսաթիվ': 'Event Date',
    'Միջոցառում': 'Event Name', 
    'Վայր': 'Venue',
    'Շարք': 'Row',
    'Տեղ': 'Seat',
    'Հայաստանի պետական ֆիլհարմոնիա': 'State Philharmonia of Armenia'
  };

  const characters: { [key: string]: string } = {
    'Հ': 'H', 'ա': 'a', 'յ': 'y', 'ս': 's', 'տ': 't', 'ն': 'n', 'ի': 'i', 
    'պ': 'p', 'ե': 'e', 'կ': 'k', 'ր': 'r', 'ան': 'an', 'ֆ': 'f', 'լ': 'l', 
    'հ': 'h', 'մ': 'm', 'ո': 'o', 'ց': 'c', 'ռ': 'rr', 'ւ': 'u',
    'Ա': 'A', 'Մ': 'M', 'ջ': 'j', 'գ': 'g', 'ծ': 'ts', 'Վ': 'V', 'ք': 'q', 
    'Տ': 'T', 'ղ': 'gh', 'Շ': 'Sh', 'վ': 'v'
  };

  let result = text;
  
  // Replace phrases first
  for (const [armenian, latin] of Object.entries(phrases)) {
    result = result.replace(new RegExp(armenian, 'g'), latin);
  }
  
  // Then replace individual characters
  for (const [armenian, latin] of Object.entries(characters)) {
    result = result.replace(new RegExp(armenian, 'g'), latin);
  }
  
  return result;
};

interface InvitationData {
  eventName: string;
  eventDate: string;
  hallName: string;
  seatInfo?: {
    row: string;
    seat: string;
  };
  language: string;
  translations: {
    invitation: string;
    youAreInvited: string;
    eventDate: string;
    eventName: string;
    venue: string;
    seat: string;
    row: string;
    accessText: string;
    presentText: string;
  };
}

export const generateInvitationPDF = async (invitationData: InvitationData, selectedSeats: any[]) => {
  try {
    const { eventName, eventDate, hallName, language, translations } = invitationData;
    
    let logoImageBytes: Uint8Array | null = null;
    
    // Try to load logo from public folder
    try {
      const logoBase64 = await loadImageAsBase64('/logo-horizontal-double.png');
      // Convert base64 to bytes for pdf-lib
      const logoData = logoBase64.split(',')[1];
      logoImageBytes = Uint8Array.from(atob(logoData), c => c.charCodeAt(0));
    } catch (error) {
      console.warn('Could not load logo image, continuing without logo:', error);
      logoImageBytes = null;
    }
    
    // Create new PDF document
    const pdfDoc = await PDFDocument.create();
    
    // Use standard fonts only - more reliable approach
    let useTransliteration = true; // Always use transliteration for maximum compatibility
    let fallbackFont: PDFFont;
    let fallbackBoldFont: PDFFont;
    
    // Embed standard fonts
    fallbackFont = await pdfDoc.embedFont(StandardFonts.Helvetica);
    fallbackBoldFont = await pdfDoc.embedFont(StandardFonts.HelveticaBold);
    
    console.log('Using transliteration approach for Armenian text to ensure compatibility');
    
    // Helper function to process text - transliterate Armenian to English
    const processText = (text: string): { processedText: string, font: PDFFont, bold?: boolean } => {
      const hasArmenian = /[\u0530-\u058F]/.test(text);
      
      if (hasArmenian) {
        return {
          processedText: transliterateArmenian(text),
          font: fallbackFont
        };
      } else {
        return {
          processedText: text,
          font: fallbackFont
        };
      }
    };
    
    // Helper for bold text
    const processTextBold = (text: string): { processedText: string, font: PDFFont } => {
      const result = processText(text);
      return {
        processedText: result.processedText,
        font: fallbackBoldFont
      };
    };
    
    // Colors
    const primaryColor = rgb(0.09, 0.22, 0.17); // #17392b
    const textColor = rgb(0.2, 0.2, 0.2); // #333333
    const grayColor = rgb(0.88, 0.88, 0.88); // #E0E0E0
    
    // Generate invitation for each selected seat or general invitation
    const seatsToGenerate = selectedSeats.length > 0 ? selectedSeats : [null];
    
    for (let i = 0; i < seatsToGenerate.length; i++) {
      const seat = seatsToGenerate[i];
      const page = pdfDoc.addPage([595.28, 841.89]); // A4 size in points
      const { width, height } = page.getSize();
      
      let currentY = height - 50; // Start from top
      
      // Header with Logo or Text
      if (logoImageBytes) {
        try {
          const logoImage = await pdfDoc.embedPng(logoImageBytes);
          const logoWidth = 280; // Set desired width in points
          const logoHeight = (logoWidth / logoImage.width) * logoImage.height;
          const logoX = (width - logoWidth) / 2;
          const logoY = currentY - logoHeight;
          
          page.drawImage(logoImage, {
            x: logoX,
            y: logoY,
            width: logoWidth,
            height: logoHeight,
          });
          
          currentY = logoY - 30;
        } catch (logoError) {
          console.warn('Error embedding logo, using text header:', logoError);
          // Fallback to text header
          const headerText = language === 'am' ? 'Հայաստանի պետական ֆիլհարմոնիա' : 'STATE PHILHARMONIA OF ARMENIA';
          const headerResult = processTextBold(headerText);
          page.drawText(headerResult.processedText, {
            x: (width - headerResult.font.widthOfTextAtSize(headerResult.processedText, 16)) / 2,
            y: currentY,
            size: 16,
            font: headerResult.font,
            color: primaryColor,
          });
          currentY -= 40;
        }
      } else {
        // Fallback: Text header if logo fails to load
        const headerText = language === 'am' ? 'Հայաստանի պետական ֆիլհարմոնիա' : 'STATE PHILHARMONIA OF ARMENIA';
        const headerResult = processTextBold(headerText);
        page.drawText(headerResult.processedText, {
          x: (width - headerResult.font.widthOfTextAtSize(headerResult.processedText, 16)) / 2,
          y: currentY,
          size: 16,
          font: headerResult.font,
          color: primaryColor,
        });
        currentY -= 40;
      }
      
      // Main title - "INVITATION" or "ՀՐԱՎԻՐԱՏՈՄՍ"
      const titleResult = processTextBold(translations.invitation);
      const titleSize = 18; // Decreased from 24 to 18
      const titleWidth = titleResult.font.widthOfTextAtSize(titleResult.processedText, titleSize);
      
      page.drawText(titleResult.processedText, {
        x: (width - titleWidth) / 2,
        y: currentY,
        size: titleSize,
        font: titleResult.font,
        color: primaryColor,
      });
      
      currentY -= 40; // Decreased from 60 to 40
      
      // Event details box with dashed border
      const boxX = 60;
      const boxWidth = width - 120;
      const boxHeight = 150;
      const boxY = currentY - boxHeight;
      
      // Draw dashed border (simple rectangle)
      const dashLength = 3;
      const gapLength = 2;
      
      // Top border (dashed line)
      let x = boxX;
      while (x < boxX + boxWidth) {
        const endX = Math.min(x + dashLength, boxX + boxWidth);
        page.drawLine({
          start: { x: x, y: boxY + boxHeight },
          end: { x: endX, y: boxY + boxHeight },
          thickness: 1,
          color: grayColor,
        });
        x += dashLength + gapLength;
      }
      
      // Bottom border (dashed line)
      x = boxX;
      while (x < boxX + boxWidth) {
        const endX = Math.min(x + dashLength, boxX + boxWidth);
        page.drawLine({
          start: { x: x, y: boxY },
          end: { x: endX, y: boxY },
          thickness: 1,
          color: grayColor,
        });
        x += dashLength + gapLength;
      }
      
      // Left border (dashed line)
      let y = boxY;
      while (y < boxY + boxHeight) {
        const endY = Math.min(y + dashLength, boxY + boxHeight);
        page.drawLine({
          start: { x: boxX, y: y },
          end: { x: boxX, y: endY },
          thickness: 1,
          color: grayColor,
        });
        y += dashLength + gapLength;
      }
      
      // Right border (dashed line)
      y = boxY;
      while (y < boxY + boxHeight) {
        const endY = Math.min(y + dashLength, boxY + boxHeight);
        page.drawLine({
          start: { x: boxX + boxWidth, y: y },
          end: { x: boxX + boxWidth, y: endY },
          thickness: 1,
          color: grayColor,
        });
        y += dashLength + gapLength;
      }
    
      
      // Event details inside the box
      let detailsY = boxY + boxHeight - 30;
      const leftMargin = boxX + 20;
      const labelX = leftMargin;
      const valueX = leftMargin + 140;
      
      // Event Name
      const eventNameLabelResult = processTextBold(`${translations.eventName}:`);
      page.drawText(eventNameLabelResult.processedText, {
        x: labelX,
        y: detailsY,
        size: 12,
        font: eventNameLabelResult.font,
        color: textColor,
      });
      const eventNameResult = processText(eventName);
      page.drawText(eventNameResult.processedText, {
        x: valueX,
        y: detailsY,
        size: 12,
        font: eventNameResult.font,
        color: textColor,
      });
      detailsY -= 25;
      
      // Event Date
      const eventDateLabelResult = processTextBold(`${translations.eventDate}:`);
      page.drawText(eventDateLabelResult.processedText, {
        x: labelX,
        y: detailsY,
        size: 12,
        font: eventDateLabelResult.font,
        color: textColor,
      });
      const eventDateResult = processText(eventDate);
      page.drawText(eventDateResult.processedText, {
        x: valueX,
        y: detailsY,
        size: 12,
        font: eventDateResult.font,
        color: textColor,
      });
      detailsY -= 25;
      
      // Venue
      const venueLabelResult = processTextBold(`${translations.venue}:`);
      page.drawText(venueLabelResult.processedText, {
        x: labelX,
        y: detailsY,
        size: 12,
        font: venueLabelResult.font,
        color: textColor,
      });
      const hallNameResult = processText(hallName);
      page.drawText(hallNameResult.processedText, {
        x: valueX,
        y: detailsY,
        size: 12,
        font: hallNameResult.font,
        color: textColor,
      });
      detailsY -= 25;
      
      // Seat information (if available)
      if (seat && seat.row !== undefined && seat.seat !== undefined) {
        const rowLabelResult = processTextBold(`${translations.row}:`);
        page.drawText(rowLabelResult.processedText, {
          x: labelX,
          y: detailsY,
          size: 12,
          font: rowLabelResult.font,
          color: textColor,
        });
        page.drawText(seat.row.toString(), {
          x: valueX,
          y: detailsY,
          size: 12,
          font: fallbackFont,
          color: textColor,
        });
        detailsY -= 25;
        
        const seatLabelResult = processTextBold(`${translations.seat}:`);
        page.drawText(seatLabelResult.processedText, {
          x: labelX,
          y: detailsY,
          size: 12,
          font: seatLabelResult.font,
          color: textColor,
        });
        page.drawText(seat.seat.toString(), {
          x: valueX,
          y: detailsY,
          size: 12,
          font: fallbackFont,
          color: textColor,
        });
      }
    }
    
    // Generate filename with timestamp
    const timestamp = new Date().toISOString().slice(0, 19).replace(/[-:T]/g, '');
    const baseFilename = useTransliteration ? 'Invitation' : (translations.invitation || 'Invitation');
    const filename = `${transliterateArmenian(baseFilename)}_${timestamp}.pdf`;
    
    // Save the PDF
    const pdfBytes = await pdfDoc.save();
    const blob = new Blob([pdfBytes], { type: 'application/pdf' });
    const url = URL.createObjectURL(blob);
    
    // Create download link
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    
    return filename;
  } catch (error) {
    console.error('Error generating PDF:', error);
    throw new Error(`Failed to generate invitation PDF: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
};

const serviceExports = {
  generateInvitationPDF
};

export default serviceExports;